<script lang="ts">
  import { createEventDispatcher } from "svelte";
  import { postJson, ServerError } from "@ui/http";
  import { validateEmail, validatePassword } from "./validations";
  import GoogleButton from "./GoogleButton.svelte";
  import { getAuthenticationContext } from "./context";
  import type { LoginResult } from "./types";
  import Recaptcha from "./Recaptcha.svelte";
  import { fade } from "svelte/transition";
  import Field from "./Field.svelte";

  export let errorCode: string | undefined = undefined;
  export let email: string = "";
  export let showReCaptcha: boolean;

  const dispatcher = createEventDispatcher();
  const context = getAuthenticationContext();

  let password: string = "";
  let showPassword: boolean = false;
  let submitting: boolean = false;
  let userLeftEmailInput: boolean = false;

  let recaptchaToken: string | undefined;
  let recaptchaComponent: Recaptcha;

  $: [emailValid, emailValidationMessage] = validateEmail(email, "login");
  $: passwordValid = validatePassword(password);
  $: canSubmit = emailValid && passwordValid && !submitting && (showReCaptcha ? recaptchaToken : true);

  async function submitClicked() {
    resetError();
    // Mark field as left to trigger required hint if still empty
    if (email.trim().length === 0) {
      userLeftEmailInput = true;
    }
    submitting = false;
    if (!canSubmit) {
      return;
    }

    try {
      submitting = true;
      const result = await postJson<LoginResult>("/app/auth/do_login_json", {
        email,
        password,
        "g-recaptcha-response": recaptchaToken
      });

      if (result.require2FA) {
        dispatcher("require2FA", result.redirectUrl);
      } else {
        dispatcher("login", result.redirectUrl);
      }
    } catch (e) {
      if (showReCaptcha && recaptchaComponent) {
        recaptchaComponent.reset();
      }
      const ex: ServerError = e as ServerError;
      if (ex.code === "trysaml" && ex.extraData?.redirect_url) {
        document.body.innerHTML = "";
        document.location.href = ex.extraData?.redirect_url;
      }
      errorCode = ex.code || "generalerror";

      if (Array.isArray(errorCode)) {
        window.requestAnimationFrame(() => {
          //@ts-ignore
          const element: HTMLElement = document.querySelector(`[name=${errorCode[0][0]}]`);
          if (element) {
            //@ts-ignore
            if (element.select) {
              //@ts-ignore
              element.select();
            }
            element.focus();
          }
        });
      }
    } finally {
      submitting = false;
    }
  }

  function resetError() {
    errorCode = undefined;
  }

  function forgetPasswordClicked() {
    resetError();
    dispatcher("modeChanged", "forgot-password");
  }

  function showPasswordClicked(evt: MouseEvent) {
    showPassword = true;
    focusPasswordInput();
  }

  function hidePasswordClicked(evt: MouseEvent) {
    showPassword = false;
    focusPasswordInput();
  }

  function focusPasswordInput() {
    window.requestAnimationFrame(() => {
      (document.querySelector("[name=password]") as HTMLInputElement)?.focus();
    });
  }

  // let hideForm = true;
</script>

<h2 class="mb-6 text-18 font-bold text-nxgray-600">Log in to your account</h2>
<GoogleButton mode="login" />

<div class="my-3 flex items-center justify-center">
  <hr class="flex-grow" />
  <span class="bg-white px-2 text-13 text-secondary">or</span>
  <hr class="flex-grow" />
</div>

<!-- <div
  on:click={() => (hideForm = false)}
  class:hidden={!hideForm}
  class="p-2 w-full text-center rounded-md border-2 border-gray-300 transition-all text-14 clickable hover:bg-gray-100">
  Continue with email and password
</div> -->
<form class="flex flex-col transition-all" on:submit|preventDefault|stopPropagation={submitClicked}>
  <Field
    showRequired={userLeftEmailInput && email.trim().length < 1}
    title="Email address"
    isValid={emailValid}
    type="text"
    name="email"
    autocomplete="email"
    bind:value={email}
    bind:userLeftInput={userLeftEmailInput}
    placeholder="name@email.com" />
  {#if emailValidationMessage}
    <div class="text-13 text-danger-600" class:visible={emailValidationMessage} class:invisible={!emailValidationMessage} transition:fade>
      {emailValidationMessage}
    </div>
  {/if}

  <div class="mt-2 flex items-center justify-between">
    <label class="mb-1 text-13 text-nxgray-600" for="password">Password</label>
    {#if !$context.hideForgotPassword}
      <button
        type="button"
        on:click|preventDefault={forgetPasswordClicked}
        class="focusable clickable -mx-2 px-2 text-13 font-medium text-teal-700 underline">Forgot my password</button>
    {/if}
  </div>

  {#if showPassword}
    <div class="relative mb-4">
      <input
        class="focusable w-full border-[#505060] text-14"
        class:valid={passwordValid}
        type="text"
        name="password"
        bind:value={password}
        placeholder="Enter your password" />
      <button
        type="button"
        class="clickable focusable absolute right-4 top-1/4 h-fit w-fit leading-none text-nxgray-400"
        on:click|stopPropagation={hidePasswordClicked}>
        <span class="material-icons pointer-events-none !text-18"> visibility_off </span>
      </button>
    </div>
  {:else}
    <div class="relative mb-4 w-full">
      <input
        class="focusable w-full border-[#505060] pr-10 text-14"
        class:valid={passwordValid}
        type="password"
        name="password"
        bind:value={password}
        placeholder="Enter your password" />
      <button
        type="button"
        class="clickable focusable absolute right-4 top-1/4 h-fit w-fit leading-none text-nxgray-400"
        on:click|stopPropagation={showPasswordClicked}>
        <span class="material-icons pointer-events-none !text-18"> visibility </span>
      </button>
    </div>
  {/if}

  {#if showReCaptcha && (email || password)}
    <div in:fade class="mb-3">
      <Recaptcha bind:token={recaptchaToken} bind:this={recaptchaComponent} />
    </div>
  {/if}
  <input
    type="submit"
    on:click|preventDefault|stopPropagation={submitClicked}
    disabled={!canSubmit}
    class="g-recaptcha hover:bg-blue-80 focusable pointer-events-none w-full select-none rounded border-blue-700 bg-blue-700 py-3 text-center text-15 font-bold text-white"
    class:opacity-30={!canSubmit}
    class:pointer-events-auto={canSubmit}
    class:clickable={canSubmit}
    value="Log in to Smore" />
</form>

<style>
  input[name="password"] {
    padding-right: 2rem !important;
  }

  input:not([type="submit"]) {
    padding-left: theme("padding.3");
    padding-right: theme("padding.3");
    padding-top: theme("padding.2");
    padding-bottom: theme("padding.2");
    border-radius: theme("borderRadius.DEFAULT");
    border-width: theme("borderWidth.DEFAULT");
    color: theme("textColor.nxgray.600");
    outline-offset: theme("outlineOffset.2");
    outline-width: theme("outlineWidth.2");
    outline-color: theme("outlineColor.transparent");
    outline-style: solid;
  }

  input:not([type="submit"]):not(.valid):not(:placeholder-shown):focus,
  input:not([type="submit"]):not(.valid):not(:placeholder-shown) {
    border-color: theme("borderColor.red.500");
  }

  input:not([type="submit"]).valid:focus,
  input:not([type="submit"]).valid {
    border-color: theme("borderColor.green.500");
    outline-color: theme("outlineColor.green.300");
  }
</style>
